import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Package, Search, Plus, Minus, BarChart3, ArrowUpDown } from 'lucide-react';
import { t } from '../i18n';

const apiUrl = process.env.REACT_APP_API_URL;

export default function WarehouseListPage() {
  const navigate = useNavigate();
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    fetchInventory();
  }, []);

    const fetchInventory = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError(t('Потребна е најава'));
        return;
      }

      const searchParams = new URLSearchParams();
      if (searchTerm) searchParams.append('search', searchTerm);
      if (locationFilter) searchParams.append('location', locationFilter);
      if (showLowStockOnly) searchParams.append('low_stock_only', 'true');

      const response = await fetch(`${apiUrl}/warehouse/inventory?${searchParams}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setInventory(data.data || []);
        setProductTotals(data.totals || []);
      } else {
        throw new Error('Failed to fetch inventory');
      }
    } catch (error) {
      console.error('Error fetching inventory:', error);
      setError(t('Неуспешно вчитување на залихите'));
      // Keep mock data as fallback
      setInventory([
        { id: 1, product_id: 1, product_name: 'Sample Product 1', sku: 'SKU001', quantity: 45.5, unit: 'pcs', location: 'A-01-01', minimum_stock: 10, stock_status: 'good' },
        { id: 2, product_id: 2, product_name: 'Sample Product 2', sku: 'SKU002', quantity: 8.0, unit: 'kg', location: 'A-01-02', minimum_stock: 10, stock_status: 'low' },
        { id: 3, product_id: 3, product_name: 'Sample Product 3', sku: 'SKU003', quantity: 25.75, unit: 'liters', location: 'B-02-01', minimum_stock: 20, stock_status: 'medium' }
      ]);
    } finally {
      setLoading(false);
    }
  };

  const filteredInventory = inventory.filter(item =>
    item.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.sku?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.location?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getStockStatus = (available, total) => {
    const percentage = (available / total) * 100;
    if (percentage < 10) return { status: t('Критично'), color: 'text-red-600 bg-red-50' };
    if (percentage < 25) return { status: t('Ниско'), color: 'text-orange-600 bg-orange-50' };
    return { status: t('Добро'), color: 'text-green-600 bg-green-50' };
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="text-lg text-gray-600">{t('Вчитување залихи...')}</div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 py-8 space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <Package className="text-purple-600" size={32} />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{t('Залихи во магацин')}</h1>
              <p className="text-gray-600">{t('Преглед и управување со залихите во магацинот')}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/warehouse/movement')}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 transition-colors"
            >
              <ArrowUpDown size={16} />
              {t('Ново движење')}
            </button>
            <button
              onClick={() => navigate('/products')}
              className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-md transition-colors"
            >
              {t('← Назад кон производи')}
            </button>
          </div>
        </div>
      </div>

      {/* Search and Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <div className="md:col-span-2">
          <div className="bg-white rounded-lg shadow p-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder={t('Пребарај по назив, SKU или локација...')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <BarChart3 className="text-blue-600" size={24} />
            <div>
              <p className="text-sm text-gray-600">{t('Вкупно ставки')}</p>
              <p className="text-2xl font-bold text-blue-600">{filteredInventory.length}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center gap-3">
            <Package className="text-green-600" size={24} />
            <div>
              <p className="text-sm text-gray-600">{t('Вкупна залиха')}</p>
              <p className="text-2xl font-bold text-green-600">
                {filteredInventory.reduce((sum, item) => sum + (item.current_stock || 0), 0)}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          {error && (
            <div className="p-4 bg-yellow-50 border border-yellow-200 text-yellow-700">
              <p>{t('Напомена: прикажани се примерни податоци - {error}', { error })}</p>
            </div>
          )}
          {filteredInventory.length > 0 ? (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('Производ')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SKU</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('Локација')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('Тековна залиха')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('Резервирано')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('Достапно')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('Статус')}</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">{t('Последно движење')}</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredInventory.map((item) => {
                  const stockStatus = getStockStatus(item.available_stock, item.current_stock);
                  return (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <Package className="text-gray-400 mr-2" size={16} />
                          <span className="text-sm font-medium text-gray-900">{item.product_name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.sku}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.location_code || item.location}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="font-medium">{item.current_stock}</span> {item.unit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.reserved_stock} {item.unit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <span className="font-medium">{item.available_stock}</span> {item.unit}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${stockStatus.color}`}>
                          {stockStatus.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(item.last_movement).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div className="text-center py-12">
              <Package size={64} className="mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">{t('Нема пронајдени залихи')}</h3>
              <p className="text-gray-500">
                {searchTerm ? t('Обидете се со други поими за пребарување.') : t('Залихите ќе се прикажат тука.')}
              </p>
            </div>
          )}
        </div>
      </div>
      </div>
    </div>
  );
}